import { Pencil, Trash2, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Tag } from '@/components/ui/Tag'
import { useSession } from '@/features/auth/api/useSession'
import { useCasePayments } from '@/features/finance/api/useFinance'
import { CarrierPaymentSheet } from '@/features/finance/components/CarrierPaymentSheet'
import { PaymentAmendSheet } from '@/features/finance/components/PaymentAmendSheet'
import { RejectPaymentSheet } from '@/features/finance/components/RejectPaymentSheet'
import { useFormat } from '@/i18n/useFormat'
import { CAP, can } from '@/lib/can'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/format'
import type { CaseLeg, Money } from '@/types/domain'

/* «Оплаты перевозчику» — one direction's money (issue #35, story 6).
 *
 * ── Four tiles, and none of them computed here ──────────────────────────────
 *
 * Согласовано, Аванс, Оплачено всего, Остаток. Every one is SERVED
 * (`finance.LegMoneyDTO`), and the advance/paid split in particular follows the
 * ATA rule on the server and nowhere else: a payment that moved before the leg
 * arrived is an advance, and a client re-deriving that from a date it happens to
 * hold would be a second definition of a figure the desk quotes to a haulier.
 * Money crosses the wire as decimal strings for AD-9's reason, and nothing on
 * this card adds two of them together.
 *
 * ── The block is served to everybody; the haulier's name is not ─────────────
 *
 * This card renders for a Sales caller too, with the counterparty column empty.
 * That is AD-5 and AD-6 doing two different jobs on one payload — figures for
 * everyone on the case, identity suppressed per field — and a card that hid the
 * tiles along with the name would break the screen for the role the money block
 * is deliberately served to.
 *
 * ── Paying is ONE act now, and this paragraph replaced its opposite ─────────
 *
 * This card used to say that paying was two sheets — a payment booked
 * unallocated, then somebody saying which cost it settled — and to offer a
 * «Провести факт» button beside every truck whose rate nobody had invoiced,
 * because a plan is not a debt and an allocation against one is refused
 * (`ErrExpenseLineIsNotADebt`). That was three writes to record one payment,
 * and each of them could land while the next was refused.
 *
 * `POST /cases/:id/legs/:legId/carrier-payment` does all three in one
 * transaction, so there is one sheet and one refusal — see
 * `CarrierPaymentSheet`, which holds the form and the rule about which body it
 * builds. What THIS card contributes is the two ways in: the header button,
 * which asks which truck, and the button on each truck's row, which already
 * knows. A truck with no invoiced rate needs no preparatory step any more: the
 * sheet recognises the debt from the agreed rate in the same request.
 *
 * ── The «не подтверждён» tag, and what it does not mean ─────────────────────
 *
 * A settlement row carries its payment's status (0059). It is a note about the
 * RECORD — Finance has not looked at it yet — and about nothing else: a pending
 * payment is inside «Оплачено», inside «Аванс» and inside «Остаток» exactly
 * like a confirmed one (spec L4). Nothing on this card filters by it, and
 * nothing may start.
 */

/* The per-truck rows and their own pay buttons used to live at the bottom of
 * this card, and they are gone ON PURPOSE: the trucks table beside it already
 * prints every truck with its three money columns, so a second truck list here
 * was the same rows said twice — and the act now sits where the reader is
 * already looking at the truck (`VehiclesTable`). What this card keeps is the
 * DIRECTION's money: the four tiles, the settlements, and the header act for
 * paying without implying a truck. */

export function LegFinance({
  caseId,
  leg,
  onRetry,
}: {
  caseId: string
  leg: CaseLeg
  /** Refetches the leg, which is where the block comes from. The card has no
   *  query of its own — the block rides the composed leg read. */
  onRetry?: () => void
}) {
  const { t } = useTranslation()
  const format = useFormat()

  const [paying, setPaying] = useState(false)
  /* The pending row Finance is deciding on: correcting it in place, or
     rejecting it with a reason. One state for the pair, because the two sheets
     are two fates of one row and can never be open together. */
  const [deciding, setDeciding] = useState<
    null | { kind: 'edit' | 'reject'; paymentId: string }
  >(null)

  const { data: user } = useSession()
  /* An affordance, not the gate: the server re-checks the capability on the
     request (AD-21). What the check buys here is a table Operations reads
     without two dead icons on every pending row. */
  const mayDecide = can(user, CAP.financePaymentConfirm)
  /* The PAYMENTS behind the settlement rows. A settlement is an allocation and
     carries the payment's facts for READING; correcting or rejecting acts on
     the payment itself, so the row's `paymentId` is resolved against the
     case's own payment list — the same rows the case's Money section shows,
     from the same cache every payment write invalidates. Read only when the
     icons could render: everybody else's page issues no extra request. */
  const casePayments = useCasePayments(mayDecide ? caseId : '')
  const decidedPayment =
    deciding !== null
      ? casePayments.data?.find((payment) => payment.id === deciding.paymentId)
      : undefined

  const money = leg.money

  if (!money) {
    /* No block, and the server said why. Both reasons — `not-configured` and
       `read-failed` — read the same way to the desk: something went wrong, try
       again. They are distinguished in the server's log, which is where the
       difference is actionable.

       A leg read through the CASE carries neither key, and renders nothing at
       all: this card belongs to the composed single-leg read, and an error box
       on every card that was never offered money would report a failure nobody
       had. */
    if (!leg.moneyUnavailable) return null

    return (
      <Card>
        <EmptyState
          icon={TriangleAlert}
          title={t('cases.legFinance.unavailableTitle')}
          description={t('cases.legFinance.unavailableDescription')}
          action={
            onRetry ? (
              <Button variant="secondary" size="sm" onClick={onRetry}>
                {t('common.actions.retry')}
              </Button>
            ) : undefined
          }
        />
      </Card>
    )
  }

  const vehicles = leg.vehicles ?? []
  const plateOf = new Map(vehicles.map((vehicle) => [vehicle.id, vehicle.plate]))

  return (
    <>
      <Card>
        <CardHeader className="flex-wrap gap-3 px-5 pt-5 sm:px-6"
          action={
            <Button variant="secondary" size="sm" onClick={() => setPaying(true)}>
              {t('cases.legFinance.payCarrier')}
            </Button>
          }
        >
          <CardTitle className="text-[18px] font-semibold">{t('cases.legFinance.title')}</CardTitle>
        </CardHeader>

        <CardBody className="px-0 pt-0 pb-0">
          {/* The four figures, in the order the desk reads them: what was
              agreed, what has gone out ahead of arrival, what has gone out in
              total, what is left. */}
          <div
            className={cn(
              'grid grid-cols-2 gap-y-5 px-5 py-6 sm:px-6 lg:grid-cols-4',
              '[&>*]:border-line [&>*]:border-l [&>*]:pr-4 [&>*]:pl-4',
              '[&>*:nth-child(2n+1)]:border-l-0 [&>*:nth-child(2n+1)]:pl-0',
              'lg:[&>*:nth-child(2n+1)]:border-l lg:[&>*:nth-child(2n+1)]:pl-4',
              'lg:[&>*:nth-child(4n+1)]:border-l-0 lg:[&>*:nth-child(4n+1)]:pl-0',
            )}
          >
            <Tile
              label={t('cases.legFinance.agreed')}
              value={money.agreedCost}
              note={t('cases.legFinance.trucks', { count: vehicles.length })}
            />
            <Tile
              label={t('cases.legFinance.advance')}
              value={money.advancePaid}
              note={t('cases.legFinance.beforeArrival')}
            />
            <Tile
              label={t('cases.legFinance.paidTotal')}
              value={money.totalPaid}
              note={t('cases.legFinance.paymentCount', { count: money.payments })}
            />
            <Tile
              label={t('cases.legFinance.outstanding')}
              value={money.outstanding}
              note={t('cases.legFinance.perVehicle', { count: money.perVehicle.length })}
              /* The one figure that carries a tone: what is still owed to a
                 haulier is what somebody is phoned about. Zero is not amber —
                 a direction that is paid up is the quiet, ordinary state. */
              tone={Number(money.outstanding.amount) > 0 ? 'attention' : undefined}
            />
          </div>

          {/* The settlements — one row per allocation against one of this
              direction's costs. The «аванс» tag is the SERVER's fact (the ATA
              rule); nothing here re-derives it from the dates beside it. */}
          {money.settlements.length === 0 ? (
            <p className="text-text-muted px-5 pb-5 text-[13px] leading-snug sm:px-6">
              {t('cases.legFinance.noSettlements')}
            </p>
          ) : (
            <div className="asa-scroll overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead className="bg-surface-sunken">
                  <tr className="border-line border-y">
                    {(['date', 'item', 'vehicle', 'method', 'amount'] as const).map(
                      (column) => (
                        <th
                          key={column}
                          scope="col"
                          className={cn(
                            'text-text-muted h-10 px-4 text-[10px] font-medium tracking-wide uppercase first:pl-6 last:pr-6',
                            column === 'amount' && 'text-right',
                          )}
                        >
                          {t(`cases.legFinance.columns.${column}`)}
                        </th>
                      ),
                    )}
                    {/* The decisions column exists only for a reader who can
                        decide — everybody else's table ends at the figure. */}
                    {mayDecide ? (
                      <th scope="col" className="h-9 px-2">
                        <span className="sr-only">
                          {t('cases.legFinance.columns.decisions')}
                        </span>
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {money.settlements.map((settlement) => (
                    <tr
                      key={settlement.id}
                      className="border-line border-b text-[13px] last:border-b-0"
                    >
                      <td className="text-text-body px-4 py-3 first:pl-6 last:pr-6 align-top whitespace-nowrap">
                        {format.dateShort(settlement.receivedOn)}
                      </td>
                      <td className="text-text-heading px-4 py-3 first:pl-6 last:pr-6 align-top">
                        {settlement.item}
                        {settlement.advance ? (
                          <Tag className="ml-1.5">{t('cases.legFinance.advanceTag')}</Tag>
                        ) : null}
                        {/* Awaiting Finance's confirmation (0059). An ATTENTION
                            tag rather than a negative one, and never a strike
                            through the figure: the money is counted in every
                            tile above regardless (L4), and what is unfinished
                            is the checking, not the payment. */}
                        {settlement.paymentStatus === 'pending' ? (
                          <Tag tone="attention" className="ml-1.5">
                            {t('cases.legFinance.pendingTag')}
                          </Tag>
                        ) : null}
                        {/* Whom we paid, when this caller may know. An empty
                            string is the withheld answer (AD-6) and prints
                            nothing rather than an empty label. */}
                        {settlement.counterpartyName ? (
                          <span className="text-text-muted block text-[12px] leading-snug">
                            {settlement.counterpartyName}
                          </span>
                        ) : null}
                      </td>
                      <td className="text-text-body px-4 py-3 first:pl-6 last:pr-6 align-top font-mono">
                        {settlement.vehicleId
                          ? (plateOf.get(settlement.vehicleId) ?? '—')
                          : '—'}
                      </td>
                      {/* The method through the catalogue the payment sheet
                          writes it with, falling back to the raw value: a
                          method added server-side must not render as a missing
                          key on a table about money. */}
                      <td className="text-text-muted px-4 py-3 first:pl-6 last:pr-6 align-top">
                        {settlement.method === 'cash' ||
                        settlement.method === 'bank_transfer'
                          ? t(`finance.payments.methods.${settlement.method}`)
                          : settlement.method}
                      </td>
                      <td className="asa-num text-text-heading px-4 py-3 first:pl-6 last:pr-6 text-right align-top">
                        {formatMoney(
                          settlement.amount.amount,
                          settlement.amount.currency,
                        )}
                      </td>
                      {/* Correct or reject — a PENDING row's two other fates
                          (0059; confirming lives on the case's payments
                          panel). A confirmed row offers neither: it is a
                          checked fact, and the way to change one is a
                          correcting entry, not an edit. Named by the payment's
                          reference, because two rows of one payment carry the
                          same icons and a screen reader hearing "Correct,
                          Correct" cannot tell which record it is about. */}
                      {mayDecide ? (
                        <td className="px-4 py-3 first:pl-6 last:pr-6 text-right align-top whitespace-nowrap">
                          {settlement.paymentStatus === 'pending' ? (
                            <span className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                aria-label={t('finance.payments.amendAction', {
                                  reference: settlement.reference,
                                })}
                                className="text-text-muted hover:text-text-heading cursor-pointer p-1"
                                onClick={() =>
                                  setDeciding({
                                    kind: 'edit',
                                    paymentId: settlement.paymentId,
                                  })
                                }
                              >
                                <Pencil size={14} aria-hidden />
                              </button>
                              <button
                                type="button"
                                aria-label={t('finance.payments.rejectAction', {
                                  reference: settlement.reference,
                                })}
                                className="text-text-muted hover:text-negative cursor-pointer p-1"
                                onClick={() =>
                                  setDeciding({
                                    kind: 'reject',
                                    paymentId: settlement.paymentId,
                                  })
                                }
                              >
                                <Trash2 size={14} aria-hidden />
                              </button>
                            </span>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </CardBody>
      </Card>

      {/* ONE sheet, one request. The three facts this card is standing on go
          with it — the case, the direction and the haulier — because the desk
          got here by opening them, and re-picking a case out of every case they
          can see is how money is booked against the wrong deal.

          The counterparty is left EMPTY when this reader may not see the
          haulier (AD-6); the sheet takes the `Maybe` and applies that rule
          itself rather than making every call site remember it. */}
      <CarrierPaymentSheet
        open={paying}
        onClose={() => setPaying(false)}
        caseId={caseId}
        legId={leg.id}
        vehicles={vehicles}
        expenses={money.expenses}
        perVehicle={money.perVehicle}
        carrierName={leg.carrierName}
      />

      {/* The decided row's two sheets, mounted once the payment behind it is
          resolved. Both are the finance components the case's payments panel
          uses, so the wording and the rules cannot drift between the two
          places a pending row is decided. */}
      {decidedPayment ? (
        <>
          <PaymentAmendSheet
            open={deciding?.kind === 'edit'}
            onClose={() => setDeciding(null)}
            payment={decidedPayment}
          />
          <RejectPaymentSheet
            open={deciding?.kind === 'reject'}
            onClose={() => setDeciding(null)}
            payment={decidedPayment}
          />
        </>
      ) : null}
    </>
  )
}

/** One tile: the figure, its label, and the phrase that says what it counts. */
function Tile({
  label,
  value,
  note,
  tone,
}: {
  label: string
  value: Money
  note: string
  tone?: 'attention'
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-text-muted text-[10px] font-medium tracking-wide uppercase">{label}</span>
      <span
        className={cn(
          'asa-num text-[22px] leading-tight font-semibold',
          tone === 'attention' ? 'text-attention' : 'text-text-heading',
        )}
      >
        {formatMoney(value.amount, value.currency)}
      </span>
      <span className="text-text-muted text-[12px] leading-snug">{note}</span>
    </div>
  )
}
